// # Exam Day Guide Component
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClockCircleOutlined, 
  EnvironmentOutlined, 
  BulbOutlined, 
  MedicineBoxOutlined,
  RocketOutlined
} from '@ant-design/icons';
import DashboardHeader from '../../../components/DashboardHeader';
import Header from '../../../landing/components/header';  // # Import regular header
import DashboardFooter from '../../../components/DashboardFooter';
import './guide_exam_day.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import { useAuthStore } from '../../../auth/state';  // # Import auth store

const ExamDayGuide = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isAuthenticated } = useAuthStore();  // # Get auth state

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Helper function to handle scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // # Add animation class to the section itself
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          
          // # Add animation to QA items
          const qaItems = entry.target.querySelectorAll('.gde_exam_day_qa-item');
          qaItems.forEach((item, i) => {
            const baseDelay = 0.1;
            const staggerDelay = i * 0.15;
            item.style.setProperty('--delay', `${baseDelay + staggerDelay}s`);
            item.style.opacity = '1';
            item.style.transform = 'translateX(0)';
            item.classList.add('gde_exam_day_animate');
          });
          
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    sectionRefs.current.forEach(section => section && observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // # Define sections with Q&A content and Ant Design icons
  const sections = [
    {
      title: 'General Information',
      icon: <MedicineBoxOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'What should I bring on exam day?',
          a: 'Valid, government-issued ID (passport recommended), scheduling permit (printed or electronic), snacks and drinks (stored in the locker)'
        },
        {
          q: 'What is the format of the USMLE Step 1 and Step 2 CK exams?',
          a: 'Step 1: 7 blocks, 40 questions per block, 60 minutes each, 8-hour total time\nStep 2 CK: 8 blocks, ~40 questions per block, 60 minutes each, 9-hour total time'
        },
        {
          q: 'How many breaks do I get?',
          a: 'You get 45 minutes of break time plus 15 minutes if you finish the tutorial early. You can distribute breaks as needed between blocks.'
        }
      ]
    },
    {
      title: 'Test Center and Logistics',
      icon: <EnvironmentOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'What time should I arrive at the test center?',
          a: 'Arrive at least 30 minutes before your scheduled time to allow check-in procedures.'
        },
        {
          q: 'How does check-in work?',
          a: 'Present ID and scheduling permit, get photographed and fingerprinted, place personal items in a locker, pass a security check before entering the testing room'
        },
        {
          q: 'Can I leave the test center during breaks?',
          a: 'No, you must stay within the test center premises during breaks.'
        },
        {
          q: 'What happens if my computer crashes during the exam?',
          a: 'Report the issue immediately. The test center will log the time lost and may extend your session.'
        }
      ]
    },
    {
      title: 'Exam Strategy',
      icon: <BulbOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'Should I flag questions for review?',
          a: 'Yes, but prioritize answering all questions first before reviewing flagged ones.'
        },
        {
          q: 'How can I manage my time during each block?',
          a: 'Aim for 90 seconds per question, if stuck mark the question and move on, reserve 5 minutes at the end for review'
        },
        {
          q: 'What if I run out of time in a block?',
          a: 'Unanswered questions are marked incorrect, so make an educated guess before time runs out.'
        }
      ]
    },
    {
      title: 'Break and Nutrition Tips',
      icon: <ClockCircleOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'When should I take breaks?',
          a: 'After 2 blocks: Short 5-7 min break\nAfter 4-5 blocks: Longer 10-15 min lunch break\nAdjust based on your stamina and needs'
        },
        {
          q: 'What should I eat during breaks?',
          a: 'Light snacks (nuts, protein bars, fruit), hydrate with water or electrolyte drinks, avoid heavy meals to prevent sluggishness'
        }
      ]
    },
    {
      title: 'Comfort and Focus',
      icon: <BulbOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'Can I use earplugs or noise-canceling headphones?',
          a: 'Yes, foam earplugs are usually allowed, and noise-canceling headphones are provided at the center.'
        },
        {
          q: 'What if I feel anxious during the exam?',
          a: 'Take deep breaths, use a quick 2-minute reset break (close eyes, stretch)'
        },
        {
          q: 'What if I feel fatigued mid-exam?',
          a: 'Take a short break, splash cold water on your face, eat a small snack for an energy boost'
        }
      ]
    },
    {
      title: 'Technical and Administrative Issues',
      icon: <MedicineBoxOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'Can I reschedule my exam on test day?',
          a: 'No, rescheduling must be done at least 5 business days in advance.'
        },
        {
          q: 'What happens if I am late?',
          a: 'If you arrive more than 30 minutes late, you may not be allowed to take the exam.'
        }
      ]
    },
    {
      title: 'Post-Exam',
      icon: <ClockCircleOutlined className="gde_exam_day_section-icon" />,
      questions: [
        {
          q: 'How soon will I get my score?',
          a: 'Score come up on the 3nd wednesday after you exam week\'s wednesday'
        },
        {
          q: 'What should I do after finishing the exam?',
          a: 'Relax and reward yourself, avoid overanalyzing questions'
        }
      ]
    }
  ];

  return (
    <div className={`gde_exam_day_page-container ${isDarkMode ? 'gde_exam_day_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}

      <div className="gde_exam_day_content-container">
        <div className="gde_exam_day_header">
          <div className="gde_exam_day_header-content">
            <div className="gde_exam_day_header-text">
              <h1>USMLE Exam Day Guide</h1>
              <p>Your comprehensive guide to ace your exam day</p>
            </div>
          </div>
        </div>

        <div className="gde_exam_day_sections">
          {sections.map((section, index) => (
            <div 
              key={index} 
              className="gde_exam_day_section"
              ref={el => sectionRefs.current[index] = el}
            >
              <div className="gde_exam_day_section-header">
                <div className="gde_exam_day_icon-container">
                  {section.icon}
                </div>
                <h2>{section.title}</h2>
              </div>
              
              <div className="gde_exam_day_qa-list">
                {section.questions.map((qa, qaIndex) => (
                  <div key={qaIndex} className="gde_exam_day_qa-item">
                    <div className="gde_exam_day_question">
                      <strong>Q{qaIndex + 1}:</strong> {qa.q}
                    </div>
                    <div className="gde_exam_day_answer">
                      {qa.a.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* # Add CTA Section */}
          <div className="gde_exam_day_cta-section">
            <div className="gde_exam_day_cta-content">
              <div className="gde_exam_day_cta-icon">
                <RocketOutlined />
              </div>
              <h2>Ready to Excel in Your USMLE Journey?</h2>
              <p>We offer personalized and perfectly-designed mentorship programs for USMLE Step 1 & 2</p>
              <button 
                className="gde_exam_day_cta-button"
                onClick={() => navigate('/products')}
              >
                Explore Our Programs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* # Pass theme prop to footer */}
      <div className="gde_exam_day_footer-wrapper">
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </div>
  );
};

export default ExamDayGuide;
